import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import LiveStatusIndicator from './LiveStatusIndicator';
import TelemetryToasts from './TelemetryToasts';
import './telemetry.css';

var MetricCards = lazy(function () {
  return import('./MetricCards');
});
var ActivityFeed = lazy(function () {
  return import('./ActivityFeed');
});
var RollingCharts = lazy(function () {
  return import('./RollingCharts');
});
var NetworkFlow = lazy(function () {
  return import('./NetworkFlow');
});
var DevConsole = lazy(function () {
  return import('./DevConsole');
});
var VisitorAnalyticsSection = lazy(function () {
  return import('./VisitorAnalyticsSection');
});

var HEADER_SLOT_ID = 'telemetry-nav-slot';
var WIDE_MQ = '(min-width: 1025px)';
var HOVER_OPEN_MS = 850;
var HOVER_CLOSE_MS = 350;
var DESKTOP_HOVER_MQ =
  '(hover: hover) and (pointer: fine) and (min-width: 1025px)';

function isWideViewport() {
  return (
    typeof window !== 'undefined' && window.matchMedia(WIDE_MQ).matches
  );
}

function canHoverExpand() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia(DESKTOP_HOVER_MQ).matches &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function getHeaderSlot() {
  if (typeof document === 'undefined') return null;
  return document.getElementById(HEADER_SLOT_ID);
}

/**
 * Self-contained telemetry UI shell.
 * Green status dot always lives in the header.
 * On wide desktop, the medium LIVE TELEMETRY widget sits in the body.
 * − collapses the expanded panel (widget stays); × dismisses the widget
 * down to the header green dot, which restores it.
 */
export default function TelemetryOverlay() {
  var _open = useState(false);
  var open = _open[0];
  var setOpen = _open[1];
  var _widgetVisible = useState(true);
  var widgetVisible = _widgetVisible[0];
  var setWidgetVisible = _widgetVisible[1];
  var _wide = useState(function () {
    return isWideViewport();
  });
  var wide = _wide[0];
  var setWide = _wide[1];
  var _headerSlot = useState(null);
  var headerSlot = _headerSlot[0];
  var setHeaderSlot = _headerSlot[1];
  var hoverOpenedRef = useRef(false);
  var openTimerRef = useRef(null);
  var closeTimerRef = useRef(null);

  useEffect(function () {
    setHeaderSlot(getHeaderSlot());
  }, []);

  useEffect(function () {
    if (typeof window === 'undefined') return undefined;
    var mq = window.matchMedia(WIDE_MQ);
    function onChange(e) {
      setWide(e.matches);
    }
    setWide(mq.matches);
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
    } else {
      mq.addListener(onChange);
    }
    return function () {
      if (mq.removeEventListener) {
        mq.removeEventListener('change', onChange);
      } else {
        mq.removeListener(onChange);
      }
    };
  }, []);

  useEffect(function () {
    return function () {
      if (openTimerRef.current) clearTimeout(openTimerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  function clearTimers() {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }

  /** − : collapse expanded panel; keep medium widget */
  function minimize() {
    clearTimers();
    hoverOpenedRef.current = false;
    setOpen(false);
  }

  /** × : remove medium widget; leave header green dot only */
  function dismiss() {
    clearTimers();
    hoverOpenedRef.current = false;
    setOpen(false);
    setWidgetVisible(false);
  }

  /** Medium widget / expand control */
  function togglePanel() {
    clearTimers();
    hoverOpenedRef.current = false;
    setOpen(function (v) {
      return !v;
    });
  }

  /** Header green dot: restore dismissed widget, or toggle panel */
  function onHeaderToggle() {
    clearTimers();
    hoverOpenedRef.current = false;
    if (!widgetVisible) {
      setWidgetVisible(true);
      setOpen(true);
      return;
    }
    setOpen(function (v) {
      return !v;
    });
  }

  function onZoneEnter() {
    if (!canHoverExpand() || !widgetVisible) return;
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (open) return;
    if (openTimerRef.current) return;
    openTimerRef.current = setTimeout(function () {
      openTimerRef.current = null;
      hoverOpenedRef.current = true;
      setOpen(true);
    }, HOVER_OPEN_MS);
  }

  function onZoneLeave() {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (!canHoverExpand() || !hoverOpenedRef.current) return;
    closeTimerRef.current = setTimeout(function () {
      closeTimerRef.current = null;
      if (!hoverOpenedRef.current) return;
      hoverOpenedRef.current = false;
      setOpen(false);
    }, HOVER_CLOSE_MS);
  }

  var showBodyWidget = wide && widgetVisible;
  var hideIdleZone = !showBodyWidget;

  var headerDot = (
    <LiveStatusIndicator
      active={open}
      minimized
      onToggle={onHeaderToggle}
    />
  );

  return (
    <div className="telemetry-root telemetry-root--reveal" data-telemetry="on">
      {headerSlot ? createPortal(headerDot, headerSlot) : null}
      <div
        className={
          'telemetry-hover-zone' +
          (open ? ' is-open' : '') +
          (hideIdleZone ? ' is-compact' : '')
        }
        onMouseEnter={onZoneEnter}
        onMouseLeave={onZoneLeave}
      >
        {showBodyWidget ? (
          <div className="telemetry-live-row">
            <LiveStatusIndicator active={open} onToggle={togglePanel} />
          </div>
        ) : null}
        {open ? (
          <aside
            id="telemetry-panel"
            className="telemetry-panel"
            aria-label="Live telemetry panel"
          >
            <header className="telemetry-panel__header">
              <div>
                <p className="telemetry-panel__eyebrow">System telemetry</p>
                <h2 className="telemetry-panel__title">Live status</h2>
              </div>
              <div className="telemetry-panel__actions">
                <button
                  type="button"
                  className="telemetry-panel__iconify"
                  onClick={minimize}
                  aria-label="Minimize panel"
                  title="Minimize"
                >
                  −
                </button>
                <button
                  type="button"
                  className="telemetry-panel__close"
                  onClick={dismiss}
                  aria-label="Dismiss telemetry widget"
                  title="Dismiss to header"
                >
                  ×
                </button>
              </div>
            </header>
            <Suspense
              fallback={
                <p className="telemetry-panel__loading">Loading live views…</p>
              }
            >
              <MetricCards />
              <div className="telemetry-panel__grid">
                <RollingCharts />
                <NetworkFlow />
              </div>
              <ActivityFeed />
              <DevConsole />
              <VisitorAnalyticsSection />
            </Suspense>
          </aside>
        ) : null}
      </div>
      <TelemetryToasts />
    </div>
  );
}
